import { Outlet, useLocation } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { isFullBleed, isSettingsRoute, isWide } from '@/lib/navigation'
import { CommandPalette } from '@/components/command-palette/CommandPalette'
import { AssistantProvider, useAssistant } from '@/components/assistant/AssistantProvider'
import { AssistantPanel } from '@/components/assistant/AssistantPanel'
import { AssistantDock } from '@/components/assistant/AssistantDock'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'

import { Header } from './Header'
import { SettingsNav } from './SettingsNav'
import { Sidebar } from './Sidebar'

const MINI_KEY = 'iws-admin:nav-mini'

const readMini = () => {
  try {
    return localStorage.getItem(MINI_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Signed-in shell: the black sidebar (full, or an icon rail, remembered) beside the white page, which runs
 * edge to edge and scrolls on its own. Small screens get a black top bar and the sidebar in a sheet.
 * Settings routes switch to settings mode (§6.10): the icon rail plus a settings column, and a narrower centred page.
 * The theme editor (`/themes`) takes the whole viewport with no admin chrome; its top bar has the way out (§8).
 */
export const DashboardLayout = () => (
  <AssistantProvider>
    <DashboardShell />
  </AssistantProvider>
)

const DashboardShell = () => {
  const { pathname } = useLocation()
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [mini, setMini] = useState(readMini)
  const fullBleed = isFullBleed(pathname)
  // The open assistant takes its own panel on the right of the frame; the page narrows beside it.
  const { isOpen: assistantOpen, panelWidth } = useAssistant()
  const wide = isWide(pathname)
  const settings = isSettingsRoute(pathname)
  // Wide editors and settings get the room: the sidebar collapses to the rail while one is open.
  const rail = mini || wide || settings
  // "‹ Settings" returns to the last page visited outside settings.
  const exitTo = useRef('/')
  useEffect(() => {
    if (!settings) exitTo.current = pathname
  }, [settings, pathname])

  useEffect(() => {
    try {
      localStorage.setItem(MINI_KEY, mini ? '1' : '0')
    } catch (error) {
      console.warn('Sidebar preference not saved', error)
    }
  }, [mini])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const openSearch = () => {
    setIsMobileOpen(false)
    setPaletteOpen(true)
  }

  return (
    <div className="min-h-screen bg-frame print:bg-card">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:p-2">
        Skip to content
      </a>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {!fullBleed && (
        <aside className={cn('no-print fixed inset-y-0 left-0 z-40 hidden transition-[width] lg:block', rail ? 'w-[60px]' : 'w-[220px]')}>
          <Sidebar mini={rail} onToggleMini={wide || settings ? undefined : () => setMini((m) => !m)} onSearch={openSearch} />
        </aside>
      )}
      {settings && (
        <aside className="no-print fixed inset-y-0 left-[60px] z-40 hidden w-[240px] border-l border-sidebar-border lg:block xl:w-[280px]">
          <SettingsNav exitTo={exitTo.current} />
        </aside>
      )}

      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side="left" className="w-[280px] border-none bg-frame p-0" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          {settings ? (
            <SettingsNav exitTo={exitTo.current} onNavigate={() => setIsMobileOpen(false)} />
          ) : (
            <Sidebar onSearch={openSearch} onNavigate={() => setIsMobileOpen(false)} />
          )}
        </SheetContent>
      </Sheet>

      <div className={cn('transition-[padding] print:p-0', assistantOpen && 'lg:pr-[var(--assistant-width)]', fullBleed ? '' : settings ? 'lg:pl-[300px] xl:pl-[340px]' : rail ? 'lg:pl-[60px]' : 'lg:pl-[220px]')} style={{ '--assistant-width': `${panelWidth}px` } as React.CSSProperties}>
        {!fullBleed && (
          <div className="no-print sticky top-0 z-30">
            <Header onMenuClick={() => setIsMobileOpen(true)} onSearch={openSearch} />
          </div>
        )}
        <main
          id="main"
          className={cn(
            'bg-page print:min-h-0 print:p-0 print:h-auto print:overflow-visible',
            fullBleed
              ? 'h-dvh overflow-hidden p-0'
              : 'min-h-[calc(100dvh-52px)] px-4 pb-16 pt-3 lg:h-dvh lg:min-h-0 lg:overflow-y-auto lg:px-4'
          )}
        >
          <div className={cn(settings ? 'mx-auto max-w-[960px] lg:pt-6' : !fullBleed && !wide && 'mx-auto max-w-[1600px]')}>
            <Outlet />
          </div>
        </main>
      </div>

      <AssistantDock />
      <AssistantPanel />
    </div>
  )
}
